import { Box, Dialog, DialogContent, Divider, Stack, styled, Typography } from '@mui/material'
import {
  CarrierVisitStatus,
  OperationType,
  WorkInstructionDto,
  WorkQueueStatus,
} from '@operations/app/api'
import { WorkQueueStatusChip } from '@operations/components/chips/WorkQueueStatusChip'
import { WorkQueue } from '@operations/features/craneSplit/models/work-queue.model'
import {
  canStartWorkQueue,
  canStopWorkQueue,
} from '@operations/features/workInstructions/helpers/work-instruction-table.helper'
import { mapStatusToWorkQueueStatus } from '@operations/features/workInstructions/helpers/work-instruction-table.mapper'
import { convertOperationType, getThemePalleteByColorName } from '@operations/utils/helper'
import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  CloseButton,
  OptionsButton,
  useMinimalsTheme,
  VisitIcon,
  YardIcon,
} from '@tom-ui/ui'
import dayjs from 'dayjs'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { WorkQueueInstructionsTable } from './WorkQueueInstructionsTable'

const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
}))

const Square = styled(Box)<{ colorname?: string }>(({ theme, colorname }) => ({
  flex: 'none',
  marginTop: '.4rem',
  height: '24px',
  width: '24px',
  borderRadius: '2px',
  background: getThemePalleteByColorName(theme, colorname!).main,
}))

interface Props {
  open: boolean
  workQueue: WorkQueue
  workInstructions: WorkInstructionDto[]
  vesselName?: string | null
  vesselVisitStatus?: CarrierVisitStatus
  onEdit: (workQueue: WorkQueue) => Promise<void> | void
  onSplit: (workQueue: WorkQueue) => void
  onRename: (workQueue: WorkQueue) => void
  onRemove: (id: number) => Promise<void> | void
  onStop: (id: number) => void
  onStart: (id: number) => void
  onClose: () => void
  getWorkInstructionForRestow: (
    containerNumber: string,
    operationType: OperationType,
  ) => WorkInstructionDto | undefined
}

export const WorkQueueDetailsDialog = observer(
  ({
    open,
    workQueue,
    workInstructions,
    vesselName,
    vesselVisitStatus,
    onRename,
    onEdit,
    onSplit,
    onRemove,
    onStart,
    onStop,
    onClose,
    getWorkInstructionForRestow,
  }: Props) => {
    const { t } = useTranslate()
    const hourFormat = 'hh:mm A'
    const dayFormat = 'MM/DD/YYYY, ' + hourFormat
    const theme = useMinimalsTheme()

    if (!workQueue) return <></>

    const workQueueStatus = computed(() => {
      const statuses = workInstructions.map(wi => wi.status!)
      return mapStatusToWorkQueueStatus(statuses)
    }).get()

    const startTimeInfo = `${dayjs(workQueue.startTime).format(dayFormat)}`

    const endTimeInfo = `${
      workQueue.endDate.getHours() < workQueue.startTime.getHours()
        ? dayjs(workQueue.endDate).format(dayFormat)
        : dayjs(workQueue.endDate).format(hourFormat)
    }`

    const bayInfo = `${t('bay', 'Bay')} ${workQueue.bay} - ${workQueue.vesselArea}`
    const canBeSplited =
      workQueue.containersLeft > 0 && workQueue.containersAmount > 1 && !workQueue.isUnknownBay
    const canBeRemoved = workQueue.canEdit && workQueue.isMergeable && !workQueue.isUnknownBay

    const actionOptions: ButtonOption[] = []

    actionOptions.push(
      {
        label:
          workQueueStatus === WorkQueueStatus.Stopped ? t('resume', 'Resume') : t('start', 'Start'),
        divider: true,
        onClick: () => onStart(+workQueue.id),
        disabled: !canStartWorkQueue(workQueueStatus, vesselVisitStatus),
        testId: 'crane-split-start-work-queue-btn',
      },
      {
        label: t('stop', 'Stop'),
        divider: true,
        onClick: () => onStop(+workQueue.id),
        disabled: !canStopWorkQueue(workQueueStatus, vesselVisitStatus),
        testId: 'crane-split-stop-work-queue-btn',
      },
      {
        label: t('edit', 'Edit'),
        divider: true,
        onClick: () => onEdit(workQueue),
        disabled: !workQueue.canEdit,
        testId: 'crane-split-edit-work-queue-btn',
      },
      {
        label: t('split', 'Split'),
        divider: true,
        onClick: () => onSplit(workQueue),
        disabled: !canBeSplited,
        testId: 'crane-split-split-work-queue-btn',
      },
      {
        label: t('rename', 'Rename'),
        divider: false,
        onClick: () => onRename(workQueue),
      },
    )

    if (canBeRemoved)
      actionOptions.push({
        label: t('remove', 'Remove'),
        divider: false,
        onClick: () => onRemove(+workQueue.id),
      })

    return (
      <Dialog onClose={onClose} open={open} fullWidth maxWidth={'xl'}>
        <DialogContent sx={{ padding: 0 }}>
          <Stack flexDirection='row'>
            <Stack sx={{ padding: theme.customSpacing.m }}>
              <Stack gap={theme.customSpacing.m}>
                <ContentContainer alignItems='flex-end'>
                  <Square colorname={workQueue.color} />

                  <Typography variant='subtitle1'>
                    {`${t(convertOperationType(workQueue.operationType))} - ${workQueue.crane}`}
                    {workQueue.name ? ` - ${workQueue.name}` : ''}
                  </Typography>
                </ContentContainer>

                <ContentContainer>
                  <Typography
                    variant='caption'
                    sx={{ whiteSpace: 'nowrap' }}
                    data-cy='crane-split-work-queue-details-time-bay-subtitle'
                  >
                    {`${startTimeInfo} - ${endTimeInfo}, ${bayInfo}`}
                  </Typography>
                </ContentContainer>

                <ContentContainer>
                  <Stack alignItems={'center'} justifyContent={'center'}>
                    <YardIcon />
                  </Stack>

                  <Typography
                    variant='caption'
                    data-cy='crane-split-work-queue-details-containers-amount'
                  >
                    {`${workQueue.containersAmountCompleted} / ${workQueue.containersAmount}`}{' '}
                    {t('containers', 'Containers')}
                  </Typography>
                </ContentContainer>

                <ContentContainer>
                  <Stack alignItems={'center'} justifyContent={'center'}>
                    <VisitIcon />
                  </Stack>

                  <Typography
                    variant='caption'
                    data-cy='crane-split-work-queue-details-moves-amount'
                  >
                    {`${workQueue.movesCompleted} / ${workQueue.moves}`} {t('moves', 'moves')}
                  </Typography>
                </ContentContainer>
              </Stack>
            </Stack>
            <Stack sx={{ padding: theme.customSpacing.m }} flexGrow={1} alignItems='flex-end'>
              <Stack flexDirection='row' justifyContent='flex-end' gap={theme.customSpacing.xs}>
                <OptionsButton
                  tooltip={t('moreOptions', 'More options')}
                  size='small'
                  options={actionOptions}
                  testId='work-queue-dialog-options-btn'
                />

                <CloseButton
                  tooltip={t('close', 'Close')}
                  size='small'
                  onClose={() => onClose()}
                  dataCy='crane-split-close-work-queue-btn'
                />
              </Stack>

              {workQueueStatus && (
                <Stack sx={{ flex: 1 }} justifyContent='center'>
                  <WorkQueueStatusChip status={workQueueStatus} />
                </Stack>
              )}
            </Stack>
          </Stack>

          <Divider />

          <Stack padding={theme.customSpacing.m}>
            <WorkQueueInstructionsTable
              workQueue={workQueue}
              workInstructions={workInstructions}
              vesselName={vesselName}
              vesselVisitStatus={vesselVisitStatus}
              getWorkInstructionForRestow={getWorkInstructionForRestow}
            />
          </Stack>
        </DialogContent>
      </Dialog>
    )
  },
)
